import React, { useState, useEffect, useCallback, useMemo } from "react";
import TopNav from "../../commonUI/TopHeader/TopHeader";
import {
  deleteAvatarsAPI,
  getUserRecordsCount,
  getUsersDrivers,
} from "../../../services/adminService";
import { showSpinner, closeSpinner, modal, alert } from "../../../js/modules/Dialogs";
import ConfirmDialog from "../../Modal/ConfirmDialog";
import { sessionExpired } from "../../../config/messages";
import {
  serverUrl,
  userImagesFolder,
  thumbSuffix,
  defaultAvatarImage,
  imageFormat,
} from "../../../config/env";
import { toScreen } from "../../../js/modules/Routing";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Pagination from "../../commonUI/Pagination/Pagination";
import SearchFilterBar from "../../commonUI/SearchFilterBar/SearchFilterBar";
import DataTable from "../../commonUI/DataTable/DataTable";
import DateTime from "../../commonUI/DateTime/DateTime";
import { MessageButtonIcon } from "../../commonUI/Icons/SvgIcon";

const Mavs = ({ userDetails }) => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(
    window?.history?.state?.usr?.selectedFilter ?? "active"
  );
  const [activeTab, setActiveTab] = useState("active");
  const [sort, setSort] = useState("name=-1");
  const [mode, setMode] = useState("list");
  const [deleteAvatars, setDeleteAvatars] = useState([]);
  const [mavs, setMavs] = useState([]);
  const [pageSize, setPageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [inputValue, setInputValue] = useState(1);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filterCount, setFilterCount] = useState({
    activeUsers: null,
    preactiveUsers: null,
    blockedUsers: null,
    deletedUsers: null,
    totalDocs: null,
  });
  const [activeFilters, setActiveFilters] = useState({
    pending: null,
    active: null,
    blocked: null,
    deleted: null,
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    getMavs();
  }, [page, pageSize, filter, sort, search, activeFilters[activeTab]]);

  useEffect(() => {
    fetchFilterCount();
  }, []);

  useEffect(() => {
    setInputValue(page);
  }, [page]);

  const getMavs = async (event) => {
    if (event) event.preventDefault();
    showSpinner();
    try {
      const resJson = await getUsersDrivers({
        filter,
        search,
        page,
        limit: pageSize,
        sort,
        ...(activeFilters[activeTab] && { customFilter: activeFilters[activeTab] }),
      });
      closeSpinner();
      setTotalPages(Math.ceil(resJson?.totalRecords / pageSize));
      setDeleteAvatars([]);
      setMavs(resJson?.data || []);
    } catch (error) {
      closeSpinner();
      if (error.response?.status === 401) {
        modal(sessionExpired);
        toScreen(false, navigate, "/logout");
      } else {
        setMavs([]);
        alert(error.message || "Error fetching mavs");
      }
    }
  };

  const fetchFilterCount = async () => {
    showSpinner();
    try {
      const resJson = await getUserRecordsCount({ userType: "mav" });
      closeSpinner();
      setFilterCount({
        activeUsers: resJson?.activeUsers,
        preactiveUsers: resJson?.preactiveUsers,
        blockedUsers: resJson?.blockedUsers,
        deletedUsers: resJson?.deletedUsers,
        totalDocs: resJson?.totalDocs,
      });
    } catch (error) {
      closeSpinner();
      if (error.response?.status === 401) {
        modal(sessionExpired);
        toScreen(false, navigate, "/logout");
      } else {
        alert(error.message || "Error fetching filter count");
      }
    }
  };

  const searchMav = useCallback(() => {
    setSearch(searchValue);
    setFilter(searchValue === "" ? "active" : null);
    setPage(1);
  }, [searchValue]);

  const sortMavs = (event, field) => {
    if (event) event.preventDefault();
    const sortFields = {
      name: { asc: "name=1", desc: "name=-1" },
      phone: { asc: "phone=1", desc: "phone=-1" },
      reviews: { asc: "reviews=1", desc: "reviews=-1" },
      quotes: { asc: "quotes=1", desc: "quotes=-1" },
      jobs: { asc: "jobs=1", desc: "jobs=-1" },
      refunds: { asc: "refunds=1", desc: "refunds=-1" },
      registeredOn: { asc: "registeredOn=1", desc: "registeredOn=-1" },
      blockedReason: { asc: "blockedReason=1", desc: "blockedReason=-1" },
      blockedOn: { asc: "blockedOn=1", desc: "blockedOn=-1" },
      deletedReason: { asc: "deletedReason=1", desc: "deletedReason=-1" },
      deletedOn: { asc: "deletedOn=1", desc: "deletedOn=-1" },
      createdAt: { asc: "createdAt=1", desc: "createdAt=-1" },
    };
    const currentSort = sortFields[field];
    if (!currentSort) return;
    setSearch("");
    setSort(sort === currentSort.asc ? currentSort.desc : currentSort.asc);
    setPage(1);
  };

  const getSortClass = (key) => {
    if (sort === `${key}=1`) return "sorting-icon sorting-icon-up";
    if (sort === `${key}=-1`) return "sorting-icon sorting-icon-down";
    return "";
  };

  const getAvatarSrc = useCallback((mav) => {
    if (mav?.accountInfo?.avatar && mav.accountInfo.avatar !== "") {
      return `${serverUrl}${userImagesFolder}/${mav.shortId}/${mav.accountInfo.avatar}${thumbSuffix}${imageFormat}`;
    }
    return `../img/${defaultAvatarImage}${imageFormat}`;
  }, []);

  const deleteAvatarToggle = useCallback((shortId) => {
    setDeleteAvatars((prev) =>
      prev.includes(shortId) ? prev.filter((id) => id !== shortId) : [...prev, shortId]
    );
  }, []);

  const deleteAvatarsHandler = useCallback(() => {
    if (deleteAvatars.length === 0) return;
    setShowDeleteConfirm(true);
  }, [deleteAvatars]);

  const handleConfirmDeleteAvatars = useCallback(async () => {
    setShowDeleteConfirm(false);
    showSpinner();
    try {
      await deleteAvatarsAPI({
        accountInfo: {
          username: userDetails.accountInfo.username,
          email: userDetails.accountInfo.email,
        },
        userType: "mav",
        deleteAvatars,
      });
      closeSpinner();
      setDeleteAvatars([]);
      getMavs();
      modal("Avatars deleted successfully", "Good job!");
    } catch (error) {
      closeSpinner();
      if (error.response?.status === 401) {
        modal(sessionExpired);
      } else {
        alert(error.message || "Error deleting avatars");
      }
    }
  }, [deleteAvatars, userDetails]);

  const handleCancelDeleteAvatars = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  const handleReset = () => {
    setActiveTab("active");
    setSort("name=-1");
    setSearchValue(null);
    setSearch("");
    setStartDate(null);
    setEndDate(null);
    setActiveFilters({ pending: null, active: null, blocked: null, deleted: null });
    setFilter("active");
    setPage(1);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
    setFilter(tab);
  };

  const SortButton = useCallback(
    ({ field, label }) => (
      <button onClick={(e) => sortMavs(e, field)} className="sort-button">
        {label}
        {sort.includes(`${field}=`) && <span className={getSortClass(field)} />}
      </button>
    ),
    [sort]
  );

  const renderMavRow = useCallback(
    (mav, index) => {
      if (mode === "avatars") {
        const isSelected = deleteAvatars.includes(mav.shortId);
        return (
          <tr key={mav?._id || index}>
            <td>
              <div
                className={`c-avatar-grid__avatar ${isSelected ? "c-avatar-grid__avatar--selected" : ""}`}
                onClick={() => deleteAvatarToggle(mav.shortId)}
              >
                <img
                  src={getAvatarSrc(mav)}
                  alt="avatar"
                  className="c-avatar__pic u-cursor-pointer"
                  onError={(e) => { e.target.src = defaultAvatarImage; }}
                />
                <div className="c-avatar-grid__icon u-before-icon-cancel"></div>
              </div>
            </td>
          </tr>
        );
      }

      return (
        <tr key={mav?._id || index}>
          <td>
            <div className="d-flex align-items-center g-12">
              <img
                src={getAvatarSrc(mav)}
                alt="avatar"
                className="avatar-pic"
                onError={(e) => { e.target.src = defaultAvatarImage; }}
              />
              <div className="text-capitalize">
                {mav?.accountInfo?.firstname || "-"}
                <br />
                <span className="text-sm text-gray-600">{mav?.shortId}</span>
              </div>
            </div>
          </td>
          <td>
            {mav?.reviews ?? 0}
            <br />
            <span className="text-sm text-gray-600">{"★ 3.5"}</span>
          </td>
          <td>{mav?.quotes ?? 0}</td>
          <td>{mav?.completedJobs ?? 0}</td>
          <td>{mav?.refunds ?? 0}</td>
          <td>
            <DateTime date={mav?.createdAt} />
          </td>

          {activeTab === "blocked" && (
            <>
              <td>{mav?.blockedReason || "-"}</td>
              <td>
                <DateTime date={mav?.blockedOn} />
              </td>
            </>
          )}

          {activeTab === "deleted" && (
            <>
              <td>{mav?.deletedReason || "-"}</td>
              <td>
                <DateTime date={mav?.deletedOn} />
              </td>
            </>
          )}

          <td>
            <div className="d-flex align-items-center g-8 justify-content-end">
              {activeTab !== "deleted" && (
                <button
                  type="button"
                  className="custom-btn"
                  onClick={(e) =>
                    toScreen(e, navigate, `/message/${mav.shortId}`, false, {
                      receiverId: mav?._id,
                      receiverShortId: mav?.shortId,
                      receiverType: mav?.userType,
                      userShortId1: "admin",
                      userShortId2: mav?.shortId,
                      username2: mav?.accountInfo?.username,
                      selectedPage: page,
                      selectedFilter: filter,
                      previousPath: window.location.pathname,
                    })
                  }
                >
                  <MessageButtonIcon />
                </button>
              )}
              <button
                type="button"
                className="custom-btn"
                onClick={(e) => toScreen(e, navigate, `/mav/${mav.shortId}`)}
              >
                View
              </button>
            </div>
          </td>
        </tr>
      );
    },
    [mode, activeTab, deleteAvatars, page, filter, navigate, deleteAvatarToggle, getAvatarSrc]
  );

  const tabConfig = useMemo(
    () => ({
      preactivation: {
        colSpan: 7,
        renderHeader: () => (
          <>
            <th><SortButton field="name" label="Driver" /></th>
            <th><SortButton field="reviews" label="Reviews" /></th>
            <th><SortButton field="quotes" label="Quotes" /></th>
            <th><SortButton field="jobs" label="Jobs" /></th>
            <th><SortButton field="refunds" label="Refunds" /></th>
            <th><SortButton field="registeredOn" label="Registered On" /></th>
            <th></th>
          </>
        ),
      },
      active: {
        colSpan: 7,
        renderHeader: () => (
          <>
            <th><SortButton field="name" label="Driver" /></th>
            <th><SortButton field="reviews" label="Reviews" /></th>
            <th><SortButton field="quotes" label="Quotes" /></th>
            <th><SortButton field="jobs" label="Jobs" /></th>
            <th><SortButton field="refunds" label="Refunds" /></th>
            <th><SortButton field="registeredOn" label="Registered On" /></th>
            <th></th>
          </>
        ),
      },
      blocked: {
        colSpan: 9,
        renderHeader: () => (
          <>
            <th><SortButton field="name" label="Driver" /></th>
            <th><SortButton field="reviews" label="Reviews" /></th>
            <th><SortButton field="quotes" label="Quotes" /></th>
            <th><SortButton field="jobs" label="Jobs" /></th>
            <th><SortButton field="refunds" label="Refunds" /></th>
            <th><SortButton field="registeredOn" label="Registered On" /></th>
            <th><SortButton field="blockedReason" label="Blocked Reason" /></th>
            <th><SortButton field="blockedOn" label="Blocked On" /></th>
            <th></th>
          </>
        ),
      },
      deleted: {
        colSpan: 9,
        renderHeader: () => (
          <>
            <th><SortButton field="name" label="Driver" /></th>
            <th><SortButton field="reviews" label="Reviews" /></th>
            <th><SortButton field="quotes" label="Quotes" /></th>
            <th><SortButton field="jobs" label="Jobs" /></th>
            <th><SortButton field="refunds" label="Refunds" /></th>
            <th><SortButton field="registeredOn" label="Registered On" /></th>
            <th><SortButton field="deletedReason" label="Deleted Reason" /></th>
            <th><SortButton field="deletedOn" label="Deleted On" /></th>
            <th></th>
          </>
        ),
      },
    }),
    [SortButton]
  );

  return (
    <>
      <TopNav title="MAVs" />

      <div className="global-body">
        <div className="global-body-container">
          <div className="custom-tabs mt-0">
            <div
              className={`tab-item ${activeTab === "preactivation" ? "active" : ""}`}
              onClick={() => handleTabChange("preactivation")}
            >
              Pending <span>({filterCount?.preactiveUsers || 0})</span>
            </div>
            <div
              className={`tab-item ${activeTab === "active" ? "active" : ""}`}
              onClick={() => handleTabChange("active")}
            >
              Active <span>({filterCount?.activeUsers || 0})</span>
            </div>
            <div
              className={`tab-item ${activeTab === "blocked" ? "active" : ""}`}
              onClick={() => handleTabChange("blocked")}
            >
              Blocked <span>({filterCount?.blockedUsers || 0})</span>
            </div>
            <div
              className={`tab-item ${activeTab === "deleted" ? "active" : ""}`}
              onClick={() => handleTabChange("deleted")}
            >
              Deleted <span>({filterCount?.deletedUsers || 0})</span>
            </div>
          </div>

          <SearchFilterBar
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            onSearchSubmit={searchMav}
            searchPlaceholder="Search..."
            filters={[
              {
                type: "dateRange",
                key: "registeredOn",
                label: "Registered on",
                value: { start: startDate, end: endDate },
                onChange: ({ start, end }) => {
                  setStartDate(start);
                  setEndDate(end);
                },
                placeholder: "Anytime",
              },
            ]}
            onReset={handleReset}
          />

          {deleteAvatars.length > 0 && (
            <div className="u-text-align-center u-margin-top-bottom-m">
              <button
                className="c-btn c-btn--small u-margin-bottom-xs c-btn--grey"
                onClick={() => setDeleteAvatars([])}
              >
                Deselect Avatars
              </button>
              <button
                className="c-btn c-btn--small u-margin-bottom-xs c-btn--red"
                onClick={deleteAvatarsHandler}
              >
                Delete Avatars
              </button>
            </div>
          )}

          <DataTable
            data={mavs}
            columns={[]}
            renderRow={renderMavRow}
            onSort={sortMavs}
            getSortClass={getSortClass}
            renderHeader={tabConfig[activeTab]?.renderHeader}
            emptyMessage="No records found"
            tableClassName="table clientTable"
            colSpan={tabConfig[activeTab]?.colSpan}
          />

          <Pagination
            pageSize={pageSize}
            totalPages={totalPages}
            page={page}
            setPage={setPage}
            inputValue={inputValue}
            setInputValue={setInputValue}
            setPageSize={setPageSize}
          />
        </div>
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete MAV Avatars"
        message={`Delete ${deleteAvatars.length} MAV avatar${deleteAvatars.length > 1 ? "s" : ""}?`}
        onConfirm={handleConfirmDeleteAvatars}
        onCancel={handleCancelDeleteAvatars}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </>
  );
};

Mavs.propTypes = {
  userDetails: PropTypes.object.isRequired,
  updateState: PropTypes.func.isRequired,
};

export default Mavs;
